/**
 * App-Einstieg + Hauptmenü (docs/10) — Stufe 3 (docs/09 §6):
 * Default-Backend ist SpacetimeDB (server-authoritativ); ?backend=local
 * erzwingt den Stufe-2-localStorage-Modus (auch automatischer Fallback,
 * wenn keine Verbindung zustande kommt). Sandbox bleibt immer lokal.
 */
import { cloneConfig, defaultConfig } from '../config/balance'
import { LocalStorageStore } from '../game/persistence'
import { newRun } from '../game/runState'
import type { RunState } from '../game/types'
import { LocalActions, type RunActions } from '../game/actions'
import { StdbRunStore } from '../net/stdbRunStore'
import { mountRun, runSummary } from './runUi'
import { mountSandbox } from './sandbox'
import { buildDebugPanel } from './debugPanel'

const app = document.getElementById('app')!
const localStore = new LocalStorageStore()
// globale Working-Config: Debug-Panel editiert sie; LOKALE neue Runs + Sandbox nutzen sie.
// STDB-Runs bekommen ihre Config vom Server-Season-Snapshot (docs/09 §4).
const workingConfig = cloneConfig(defaultConfig)

const params = new URLSearchParams(location.search)
const wantLocal = params.get('backend') === 'local'
// Default: https (battle.nextpeak.at) -> Traefik-Route; http (LAN :3290) -> nginx-Proxy :3600
const STDB_URI = import.meta.env.VITE_STDB_URI ??
  (location.protocol === 'https:' ? 'wss://stdb.nextpeak.at' : `ws://${location.hostname}:3600`)
const STDB_DB = import.meta.env.VITE_STDB_DB ?? 'autobattler'

// ---------- Verbindungs-Banner (nur STDB-Modus) ----------
const banner = document.createElement('div')
banner.id = 'netBanner'
banner.style.cssText =
  'position:fixed;top:0;left:50%;transform:translateX(-50%);z-index:9999;display:none;' +
  'padding:4px 14px;border-radius:0 0 8px 8px;font-size:13px;color:#fff;background:#a33;'
document.body.appendChild(banner)

function showBanner(text: string | null): void {
  banner.style.display = text ? '' : 'none'
  banner.textContent = text ?? ''
}

// ---------- Backend-Abstraktion ----------
interface Backend {
  kind: 'local' | 'stdb'
  load(): RunState | null
  newRun(seed: number): Promise<RunState>
  actionsFor(state: RunState): RunActions
  subscribeExternal?(state: RunState, cb: () => void): () => void
}

const localBackend: Backend = {
  kind: 'local',
  load: () => localStore.load(),
  newRun: (seed: number) => {
    localStore.clear()
    const state = newRun(seed, workingConfig)
    localStore.save(state)
    return Promise.resolve(state)
  },
  actionsFor: (state: RunState) => new LocalActions(state, localStore),
}

function stdbBackend(store: StdbRunStore): Backend {
  return {
    kind: 'stdb',
    load: () => store.current(),
    newRun: (seed: number) => store.createRun(seed),
    actionsFor: (state: RunState) => store.actionsFor(state),
    subscribeExternal: (state: RunState, cb: () => void) => store.attach(state, cb),
  }
}

let cleanup: (() => void) | null = null

function switchTo(mount: () => (() => void) | null): void {
  if (cleanup) cleanup()
  cleanup = mount()
}

function openRun(backend: Backend, state: RunState): void {
  switchTo(() =>
    mountRun(app, {
      state,
      actions: backend.actionsFor(state),
      onExit: () => showMenu(backend),
      subscribeExternal: backend.subscribeExternal
        ? cb => backend.subscribeExternal!(state, cb)
        : undefined,
    }),
  )
}

function showMenu(backend: Backend): void {
  switchTo(() => {
    const saved = backend.load()
    const modeBadge = backend.kind === 'stdb'
      ? '<p class="hint" style="margin-top:4px">☁ Server-Modus (SpacetimeDB) — Fortschritt geräteübergreifend</p>'
      : '<p class="hint" style="margin-top:4px">💾 Lokaler Modus — Fortschritt nur in diesem Browser</p>'
    app.innerHTML = `
      <div class="menu-screen">
        <div class="menu-box">
          <h1 class="menu-logo">⚔️ AUTOBATTLER</h1>
          <p class="menu-tagline">Break the game. Have fun doing it.</p>
          ${modeBadge}
          ${saved ? `
            <div class="run-card">
              <b>Pausierter Run</b>
              <span>${runSummary(saved)}</span>
            </div>
            <button id="mResume" class="primary big">▶ Fortsetzen</button>
            <button id="mNewRun" class="big">Neuer Run starten</button>
          ` : `
            <button id="mNewRun" class="primary big">▶ Neuer Run starten</button>
          `}
          <button id="mSandbox" class="big">🧪 Combat-Sandbox (Stufe 1)</button>
          <button class="big" disabled title="Kommt mit Stufe 4 (Matchmaking/Ladder)">🏆 Season & Ladder <span class="badge-locked">gesperrt</span></button>
          <button id="mSettings" class="big">⚙ Einstellungen</button>
        </div>
        <div id="mSettingsModal" class="overlay hidden">
          <div class="box settings-box">
            <h2>Einstellungen — Balance-Config</h2>
            <p class="hint">${backend.kind === 'stdb'
              ? 'Wirkt nur auf Sandbox-Kämpfe und den lokalen Modus (?backend=local). Server-Runs nutzen den Season-Snapshot.'
              : 'Wirkt auf neue Runs und Sandbox-Kämpfe. Ein laufender Run behält seinen Config-Snapshot.'}</p>
            <div id="mDebugPanel" class="settings-panel"></div>
            <div class="row" style="justify-content:center"><button id="mCloseSettings" class="primary">Schließen</button></div>
          </div>
        </div>
      </div>
    `
    const resume = document.getElementById('mResume')
    if (resume) {
      resume.addEventListener('click', () => {
        const state = backend.load()
        if (state) openRun(backend, state)
      })
    }
    document.getElementById('mNewRun')!.addEventListener('click', () => {
      if (saved && !confirm('Aktueller Run wird aufgegeben. Wirklich neu starten?')) return
      const seed = Math.floor(Math.random() * 0xffffffff) >>> 0
      backend.newRun(seed).then(
        state => openRun(backend, state),
        err => alert(`Neuer Run fehlgeschlagen: ${err instanceof Error ? err.message : err}`),
      )
    })
    document.getElementById('mSandbox')!.addEventListener('click', () => {
      switchTo(() => mountSandbox(app, workingConfig, () => showMenu(backend)))
    })
    document.getElementById('mSettings')!.addEventListener('click', () => {
      buildDebugPanel(document.getElementById('mDebugPanel')!, workingConfig, defaultConfig)
      document.getElementById('mSettingsModal')!.classList.remove('hidden')
    })
    document.getElementById('mCloseSettings')!.addEventListener('click', () => {
      document.getElementById('mSettingsModal')!.classList.add('hidden')
    })
    return null
  })
}

// ---------- Boot ----------
async function boot(): Promise<void> {
  if (wantLocal) {
    showMenu(localBackend)
    return
  }
  app.innerHTML = '<div class="menu-screen"><div class="menu-box"><h1 class="menu-logo">⚔️ AUTOBATTLER</h1><p class="menu-tagline">Verbinde mit Server…</p></div></div>'
  const store = new StdbRunStore(STDB_URI, STDB_DB, s => {
    showBanner(s.online ? null : `⚠ ${s.detail}`)
  })
  try {
    await store.connect()
    const backend = stdbBackend(store)
    // BATTLE-23: Server-Run verschwunden (abandon im anderen Tab / Version-Skew)
    // -> zurueck ins Menue statt Phantom-Run weiterspielen
    store.onRunGone = () => {
      showBanner('⚠ Run auf dem Server beendet — zurück im Menü')
      setTimeout(() => showBanner(null), 6000)
      showMenu(backend)
    }
    showMenu(backend)
  } catch (err) {
    console.warn('[stdb] Verbindung fehlgeschlagen — lokaler Modus', err)
    showBanner('⚠ Kein Server erreichbar — lokaler Modus (Fortschritt nur in diesem Browser)')
    showMenu(localBackend)
  }
}

void boot()
